import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ClientRow from './ClientRow';
import { allPass, compose, equals, filter, find, prop, propEq, propSatisfies, sortBy, toLower } from 'rambda';
import { ToastContainer } from 'react-toastify';
import NewClientModal from './NewClient';
import { ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import ClientsLoadingPage from './ClientListLoading';

const allClientsUrl = '/client/v1/allClients';

const isActive = propSatisfies(equals(1), 'status');
const isNotActive = propSatisfies(equals(0), 'status');
const isClient = propSatisfies(equals(1), 'isClient');
const isNotClient = propSatisfies(equals(0), 'isClient');
const activeClients = allPass([isActive, isClient]);
const disabledClients = allPass([isNotActive, isClient]);
const notClients = allPass([isActive, isNotClient]);
const namePropCompose = compose(toLower, prop('client_id'));
const sortEm = sortBy(compose(namePropCompose));

const tabs = [
	{ name: 'Active', href: '#', current: true },
	{ name: 'Non-clients', href: '#', current: false },
	{ name: 'Disabled', href: '#', current: false },
	{ name: 'All', href: '#', current: false },
];

function classNames(...classes) {
	return classes.filter(Boolean).join(' ');
}

/**
 *
 * @param clients
 * @param setClients
 * @returns {JSX.Element}
 * @constructor
 */
const ClientList = ({ clients, setClients }) => {
	const [showNew, setShowNew] = useState(false);
	const [theClients, setTheClients] = useState(clients);
	let navigate = useNavigate();

	useEffect(() => {
		const sorted = sortEm(clients);
		setTheClients(sorted);
	}, [clients]);

	function handleAddClick(id) {
		setShowNew(true);
	}

	function onClickFilter(e) {
		const item = find(propEq('current', true), tabs);
		item.current = false;

		const newCurrent = find(propEq('name', e.target.innerText), tabs);
		if (newCurrent) {
			newCurrent.current = true;
		}

		let newClientList;

		if (e.target.innerText === 'Active') {
			newClientList = sortEm(filter(activeClients, clients));
		} else if (e.target.innerText === 'Non-clients') {
			newClientList = sortEm(filter(notClients, clients));
		} else if (e.target.innerText === 'Disabled') {
			newClientList = sortEm(filter(disabledClients, clients));
		} else if (e.target.innerText === 'All') {
			newClientList = sortEm(clients);
		}

		setTheClients(newClientList);
	}

	function handleBack() {
		navigate(-1);
	}

	return (
		<>
			<ToastContainer />
			{showNew && <NewClientModal open={showNew} setOpen={setShowNew} />}
			<div className="py-4 px-4 sm:px-6 lg:px-8">
				<div className="pb-4" onClick={handleBack}>
					<span className="text-indigo-600">
						<ArrowUturnLeftIcon className="h-6 w-6 text-indigo-600" />
					</span>
				</div>
				<h1 className="py-4 text-xl font-semibold text-gray-900">Clients</h1>
				<div className="block sm:w-1/2">
					<nav className="isolate flex divide-x divide-gray-200 " aria-label="Tabs">
						{tabs.map((tab, tabIdx) => (
							<div
								key={tab.name}
								// href={tab.href}
								className={classNames(
									tab.current
										? 'text-gray-50 bg-indigo-600'
										: 'text-gray-500 hover:text-gray-700 hover:bg-gray-50',
									tabIdx === 0 ? 'rounded-l-md' : '',
									tabIdx === tabs.length - 1 ? 'rounded-r-md' : '',
									'group relative -ml-px inline-flex items-center border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500',
								)}
								onClick={e => {
									onClickFilter(e);
								}}
								aria-current={tab.current ? 'page' : undefined}>
								<span>{tab.name}</span>
								<span
									aria-hidden="true"
									className={classNames(
										tab.current ? 'bg-indigo-500' : 'bg-transparent',
										'absolute inset-x-0 bottom-0 h-0.5',
									)}
								/>
							</div>
						))}
					</nav>
				</div>

				<div className="mt-4">
					<button
						onClick={() => {
							handleAddClick();
						}}
						type="button"
						className="inline-flex items-end justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto">
						Add Client
					</button>
				</div>

				<div className="-mx-4 mt-8 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:-mx-6 md:mx-0 md:rounded-lg w-3/4">
					<table className="table-fixed min-w-full divide-y divide-gray-300">
						<thead className="bg-gray-200">
							<tr>
								<th
									scope="col"
									className="py-3.5 pl-4 pr-3 text-left text-sm font-semi-bold text-gray-900 sm:pl-6 w-2/4">
									Name
								</th>
								<th
									scope="col"
									className="hidden px-3 py-3.5 text-left text-sm font-semi-bold text-gray-900 lg:table-cell w-1/4">
									Active Users
								</th>
								<th
									scope="col"
									className="hidden px-3 py-3.5 text-left text-sm font-semi-bold text-gray-900 lg:table-cell w-1/4">
									Client Status
								</th>
								<th
									scope="col"
									className="hidden px-3 py-3.5 text-left text-sm font-semi-bold text-gray-900 lg:table-cell w-1/4">
									Subscription
								</th>
								<th scope="col"></th>
							</tr>
						</thead>
						<tbody className="divide-y divide-gray-200 bg-white">
							{theClients.map(client => (
								<ClientRow key={client.id} client={client} clients={clients} setClients={setClients} />
							))}
						</tbody>
					</table>
				</div>
			</div>
		</>
	);
};

/**
 *
 * @returns {JSX.Element}
 * @constructor
 */
const ClientsListPage = () => {
	const [clients, setClients] = useState(null);

	useEffect(() => {
		getClients().then();
	}, []);

	const getClients = async () => {
		return await axios
			.get(allClientsUrl)
			.then(response => {
				setClients(response.data);
			})
			.catch(error => {
				console.log(error);
			});
	};

	if (!clients) {
		return <ClientsLoadingPage />;
	}

	if (clients) {
		return <ClientList clients={clients} setClients={setClients} />;
	}
};

export default ClientsListPage;
